import React, { useState, useEffect } from 'react'
import { Button, Modal, Space, Select, Input, DatePicker } from 'antd'
import PropTypes from 'prop-types'
import { withRouter, Switch } from 'react-router-dom'
import PageLoader from '../../components/Layouts/PageLoader'
import FilterTable from '../../components/Forms/searchFilter'
import {
  getRequest,
  formatUrl,
  customDutiesUrl,
  customDutyRegenerateDocumentUrl,
  errorNotification,
  deleteRequest,
  successNotification,
  postRequest
} from '../../config'
import { columns } from './tableColumns'
import FormPage from './Form'
import AIFormPage from './AIForm'
import UpdatePage from './Update'
import PrivateRoute from '../../hocs/PrivateRoute'

const { RangePicker } = DatePicker

// Duty request processing statuses (match custom_duties.constants.DUTY_PROCESSING_STATUS).
const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'COMPLETED', label: 'Completed' }
]

// Build a querystring from active filters, omitting blanks. Backend supports status,
// created_after/created_before (ISO), and search (chassis/trim/make/model).
const buildQuery = ({ status, search, range }) => {
  const params = new URLSearchParams()
  if (status) params.append('status', status)
  if (search) params.append('search', search)
  if (range && range[0]) params.append('created_after', range[0].toISOString())
  if (range && range[1]) params.append('created_before', range[1].toISOString())
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

const CustomDutiesList = props => {
  const [loading, setLoading] = useState(false)
  const [carDetails, setCarDetails] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAICreateModal, setShowAICreateModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [currentCarDetail, setCurrentCarDetail] = useState({})
  const [modalKey, setModalKey] = useState(0)
  const [regeneratingId, setRegeneratingId] = useState(null)
  const [status, setStatus] = useState(undefined)
  const [search, setSearch] = useState('')
  const [range, setRange] = useState(null)

  // `url` is a paginator next/previous link when paging; it already carries the active
  // filters + page param, so fetch it directly. Otherwise build from current filters (page 1).
  const getCarDetails = async (filters = { status, search, range }, url = null) => {
    const response = await getRequest(
      url ? formatUrl(url) : `${customDutiesUrl}${buildQuery(filters)}`,
      'custom duties',
      setLoading
    )

    if (response.success) {
      setCarDetails(response.data.results || response.data)
      setMeta({ ...response.data })
    } else {
      if (response.error.response) {
        errorNotification(
          response.error.response.data.message || 'Error fetching data'
        )
      }
    }
  }

  const applyFilters = () => getCarDetails({ status, search, range })

  const clearFilters = () => {
    setStatus(undefined)
    setSearch('')
    setRange(null)
    getCarDetails({ status: undefined, search: '', range: null })
  }

  const handleDelete = async () => {
    const response = await deleteRequest(
      `${customDutiesUrl}/${currentCarDetail.id}/`,
      'custom duty',
      setLoading
    )
    if (response.success) {
      successNotification('Request deleted successfully')
      setShowDeleteModal(false)
      getCarDetails()
    }
  }

  const handleRegenerateDocument = async record => {
    setRegeneratingId(record.id)
    const response = await postRequest(
      customDutyRegenerateDocumentUrl(record.id),
      {},
      'duty document',
      () => {}
    )
    setRegeneratingId(null)
    if (response.success) {
      successNotification('Document updated')
      getCarDetails()
    } else {
      errorNotification(
        response.error?.response?.data?.message ||
          'Could not update the document, retry.'
      )
    }
  }

  useEffect(() => {
    getCarDetails()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (showCreateModal || showAICreateModal || showUpdateModal) {
      setModalKey(modalKey + 1)
    }
    // eslint-disable-next-line
  }, [showCreateModal, showAICreateModal, showUpdateModal])

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 16 }}>
            Custom Duty Requests {meta && `(${meta.count})`}
          </h3>
          <div style={{ marginBottom: 16 }}>
            <Space wrap>
              <Button type="primary" onClick={() => setShowAICreateModal(true)}>
                New Request (AI)
              </Button>
              <Button onClick={() => setShowCreateModal(true)}>
                New Request (Manual)
              </Button>
            </Space>
          </div>
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
            />
            <RangePicker
              value={range}
              onChange={setRange}
              placeholder={['From', 'To']}
            />
            <Input.Search
              allowClear
              placeholder="Search make / model / chassis"
              style={{ width: 250 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onSearch={applyFilters}
            />
            <Button type="primary" onClick={applyFilters}>
              Apply
            </Button>
            <Button onClick={clearFilters}>Clear</Button>
            <Button onClick={applyFilters}>Refresh</Button>
          </Space>

          <FilterTable
            dataSource={carDetails}
            columns={columns(
              setShowUpdateModal,
              setCurrentCarDetail,
              setShowDeleteModal,
              handleRegenerateDocument,
              regeneratingId
            )}
            meta={meta}
            getData={url => getCarDetails({ status, search, range }, url)}
          />
        </div>
      </div>

      <Modal
        title="Create Custom Duty Request"
        centered
        open={showCreateModal}
        onOk={() => setShowCreateModal(false)}
        onCancel={() => setShowCreateModal(false)}
        footer={null}
        width={1000}
      >
        <FormPage
          key={modalKey}
          handleCancel={() => {
            setShowCreateModal(false)
            getCarDetails()
          }}
        />
      </Modal>

      <Modal
        title="Create Custom Duty Request (AI)"
        centered
        open={showAICreateModal}
        onOk={() => setShowAICreateModal(false)}
        onCancel={() => setShowAICreateModal(false)}
        footer={null}
        width={1000}
      >
        <AIFormPage
          key={modalKey}
          handleCancel={() => {
            setShowAICreateModal(false)
            getCarDetails()
          }}
        />
      </Modal>

      <Modal
        title="Update Custom Duty Request"
        centered
        open={showUpdateModal}
        onOk={() => setShowUpdateModal(false)}
        onCancel={() => setShowUpdateModal(false)}
        footer={null}
        width={1000}
      >
        <UpdatePage
          key={modalKey}
          carDetail={currentCarDetail}
          handleCancel={() => {
            setShowUpdateModal(false)
            getCarDetails()
          }}
        />
      </Modal>

      <Modal
        title="Delete Custom Duty Request"
        centered
        open={showDeleteModal}
        onOk={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>
          Are you sure you want to delete this request for{' '}
          <b>
            {`${currentCarDetail.carYear || ''} ${currentCarDetail.make?.name ||
              ''} ${currentCarDetail.model?.name || ''}`.toUpperCase()}
          </b>
          ?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>
    </PageLoader>
  )
}

CustomDutiesList.propTypes = {
  history: PropTypes.object
}

const CustomDutiesRoutes = props => (
  <Switch>
    <PrivateRoute exact path={props.match.path} component={CustomDutiesList} />
  </Switch>
)

CustomDutiesRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(CustomDutiesRoutes)
